// @mui
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------
// These helpers return precise, self-describing style shapes rather than the library's
// CSSObject: the results are spread into emotion objects, sx props and GlobalStyles alike,
// and a concrete shape stays assignable to all of them.

type BgBlurProps = {
    color?: string;
    blur?: number;
    opacity?: number;
    imgUrl?: string;
};

type BgBlurStyles = {
    position?: 'relative';
    backgroundImage?: string;
    backdropFilter?: string;
    WebkitBackdropFilter?: string;
    backgroundColor?: string;
    '&:before'?: {
        position: 'absolute';
        top: number;
        left: number;
        zIndex: number;
        content: string;
        width: string;
        height: string;
        backdropFilter: string;
        WebkitBackdropFilter: string;
        backgroundColor: string;
    };
};

export function bgBlur(props?: BgBlurProps): BgBlurStyles {
    const color = props?.color || '#000000';
    const blur = props?.blur || 6;
    const opacity = props?.opacity || 0.8;
    const imgUrl = props?.imgUrl;

    if (imgUrl) {
        return {
            position: 'relative',
            backgroundImage: `url(${ imgUrl })`,
            '&:before': {
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 9,
                content: '""',
                width: '100%',
                height: '100%',
                backdropFilter: `blur(${ blur }px)`,
                WebkitBackdropFilter: `blur(${ blur }px)`,
                backgroundColor: alpha(color, opacity),
            },
        };
    }

    return {
        backdropFilter: `blur(${ blur }px)`,
        WebkitBackdropFilter: `blur(${ blur }px)`,
        backgroundColor: alpha(color, opacity),
    };
}

// ----------------------------------------------------------------------

type BgGradientProps = {
    direction?: string;
    startColor?: string;
    endColor?: string;
    imgUrl?: string;
    color?: string;
};

type BgGradientStyles = {
    background: string;
    backgroundSize?: string;
    backgroundRepeat?: string;
    backgroundPosition?: string;
};

export function bgGradient(props?: BgGradientProps): BgGradientStyles {
    const direction = props?.direction || 'to bottom';
    const startColor = props?.startColor;
    const endColor = props?.endColor;
    const imgUrl = props?.imgUrl;
    const color = props?.color;

    if (imgUrl) {
        return {
            background: `linear-gradient(${ direction }, ${ startColor || color }, ${ endColor || color }), url(${ imgUrl })`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
        };
    }

    return {
        background: `linear-gradient(${ direction }, ${ startColor }, ${ endColor })`,
    };
}

// ----------------------------------------------------------------------

type TextGradientStyles = {
    background: string;
    WebkitBackgroundClip: 'text';
    WebkitTextFillColor: 'transparent';
};

export function textGradient(value: string): TextGradientStyles {
    return {
        background: `-webkit-linear-gradient(${ value })`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    };
}

// ----------------------------------------------------------------------

type FilterStyles = {
    filter: string;
    WebkitFilter: string;
    MozFilter: string;
};

export function filterStyles(value: string): FilterStyles {
    return {
        filter: value,
        WebkitFilter: value,
        MozFilter: value,
    };
}

// ----------------------------------------------------------------------

export const hideScrollbarY = {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
} as const;

// ----------------------------------------------------------------------

export const hideScrollbarX = {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    overflowX: 'scroll',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
} as const;
